import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings as SettingsIcon, Save, RefreshCw, AlertCircle,
  Smartphone, Users, Shield, ToggleLeft, ToggleRight,
  UserPlus, Edit, Trash2, Key, Radio, Eye, EyeOff,
  CheckCircle, XCircle, ShieldCheck, ShieldOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export function Settings() {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getSettings();
      setSettings(data);
      setHasChanges(false);
    } catch (err) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTierChange = (tier, field, value) => {
    setSettings(prev => ({
      ...prev,
      tier_limits: {
        ...prev.tier_limits,
        [tier]: {
          ...prev.tier_limits?.[tier],
          [field]: parseInt(value) || 0
        }
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.updateSettings(settings);
      toast.success('Settings saved successfully');
      setHasChanges(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">Configure system defaults and limits</p>
          </div>
        </div>
        <div className="grid gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="border-border bg-card">
              <CardContent className="p-6">
                <div className="h-32 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">Configure system defaults and limits</p>
          </div>
        </div>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Failed to load settings</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" onClick={fetchSettings}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">System configuration and admin management</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="system" className="space-y-6">
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="system" className="data-[state=active]:bg-primary">
            <SettingsIcon className="mr-2 h-4 w-4" />
            System
          </TabsTrigger>
          <TabsTrigger value="admins" className="data-[state=active]:bg-primary">
            <Users className="mr-2 h-4 w-4" />
            Admin Users
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-primary">
            <Shield className="mr-2 h-4 w-4" />
            2FA Settings
          </TabsTrigger>
        </TabsList>

        {/* System Settings Tab */}
        <TabsContent value="system" className="space-y-6">
          <div className="flex justify-end">
            {hasChanges && (
              <Badge variant="outline" className="text-yellow-500 border-yellow-500 mr-3">
                Unsaved Changes
              </Badge>
            )}
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || isSaving}
              data-testid="save-settings-btn"
            >
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            Global Device Limits
          </CardTitle>
          <CardDescription>
            Default limits applied when no tier or user override is set
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="default_device_limit">Default Device Limit</Label>
              <Input
                id="default_device_limit"
                type="number"
                min="1"
                max="20"
                value={settings?.default_device_limit || 4}
                onChange={(e) => handleChange('default_device_limit', parseInt(e.target.value) || 4)}
                data-testid="default-device-limit-input"
              />
              <p className="text-xs text-muted-foreground">
                Max devices a user can register
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_concurrent_limit">Default Concurrent Streams</Label>
              <Input
                id="default_concurrent_limit"
                type="number"
                min="1"
                max="10"
                value={settings?.default_concurrent_limit || 4}
                onChange={(e) => handleChange('default_concurrent_limit', parseInt(e.target.value) || 4)}
                data-testid="default-concurrent-limit-input"
              />
              <p className="text-xs text-muted-foreground">
                Max simultaneous streams per user
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier Limits */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Tier Limits
          </CardTitle>
          <CardDescription>
            Override defaults per membership tier (for future web app)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {['engineer', 'conductor', 'fireman'].map(tier => (
              <div key={tier} className="flex items-center gap-6 p-4 rounded-lg bg-secondary/30">
                <div className="w-28">
                  <Badge 
                    variant="outline" 
                    className={`capitalize ${
                      tier === 'engineer' ? 'border-green-500 text-green-500' :
                      tier === 'conductor' ? 'border-blue-500 text-blue-500' :
                      'border-orange-500 text-orange-500'
                    }`}
                  >
                    {tier}
                  </Badge>
                  {tier === 'engineer' && (
                    <p className="text-[10px] text-muted-foreground mt-1">Apps + Web</p>
                  )}
                  {tier === 'conductor' && (
                    <p className="text-[10px] text-muted-foreground mt-1">Web only</p>
                  )}
                  {tier === 'fireman' && (
                    <p className="text-[10px] text-muted-foreground mt-1">Web only</p>
                  )}
                </div>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Devices</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={settings?.tier_limits?.[tier]?.devices || 4}
                      onChange={(e) => handleTierChange(tier, 'devices', e.target.value)}
                      className="h-8"
                      data-testid={`tier-${tier}-devices-input`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Concurrent</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={settings?.tier_limits?.[tier]?.concurrent || 4}
                      onChange={(e) => handleTierChange(tier, 'concurrent', e.target.value)}
                      className="h-8"
                      data-testid={`tier-${tier}-concurrent-input`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Feature Flags */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Enforcement
          </CardTitle>
          <CardDescription>
            Enable or disable limit enforcement globally
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div>
              <p className="font-medium">Enforce Device Limits</p>
              <p className="text-sm text-muted-foreground">
                Block device registration when limit reached
              </p>
            </div>
            <Switch
              checked={settings?.enforce_device_limits ?? true}
              onCheckedChange={(checked) => handleChange('enforce_device_limits', checked)}
              data-testid="enforce-device-limits-switch"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div>
              <p className="font-medium">Enforce Concurrent Limits</p>
              <p className="text-sm text-muted-foreground">
                Block new streams when limit reached
              </p>
            </div>
            <Switch
              checked={settings?.enforce_concurrent_limits ?? true}
              onCheckedChange={(checked) => handleChange('enforce_concurrent_limits', checked)}
              data-testid="enforce-concurrent-limits-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Ads Configuration */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-primary" />
            Ads Configuration
          </CardTitle>
          <CardDescription>
            Configure ad settings for OTT and mobile apps
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
            <div>
              <p className="font-medium">Ads Enabled</p>
              <p className="text-sm text-muted-foreground">
                Enable VAST ad serving in apps
              </p>
            </div>
            <Switch
              checked={settings?.ads_enabled ?? false}
              onCheckedChange={(checked) => handleChange('ads_enabled', checked)}
              data-testid="ads-enabled-switch"
            />
          </div>

          {/* Preroll Settings */}
          <div className="p-4 rounded-lg bg-secondary/30 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Preroll Ads</p>
                <p className="text-sm text-muted-foreground">
                  Show ad before stream starts
                </p>
              </div>
              <Switch
                checked={settings?.preroll_enabled ?? true}
                onCheckedChange={(checked) => handleChange('preroll_enabled', checked)}
                data-testid="preroll-enabled-switch"
              />
            </div>
          </div>

          {/* Still Watching Settings */}
          <div className="p-4 rounded-lg bg-secondary/30 space-y-4">
            <p className="font-medium">Still Watching Prompt</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="still_watching_interval">Interval (minutes)</Label>
                <Input
                  id="still_watching_interval"
                  type="number"
                  min="15"
                  max="180"
                  value={settings?.still_watching_interval_minutes ?? 60}
                  onChange={(e) => handleChange('still_watching_interval_minutes', parseInt(e.target.value) || 60)}
                  data-testid="still-watching-interval-input"
                />
                <p className="text-xs text-muted-foreground">
                  Time before "Still Watching?" prompt (15-180 min)
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Ad with Prompt</Label>
                  <p className="text-xs text-muted-foreground">
                    Play midroll ad when user confirms
                  </p>
                </div>
                <Switch
                  checked={settings?.still_watching_ad_enabled ?? true}
                  onCheckedChange={(checked) => handleChange('still_watching_ad_enabled', checked)}
                  data-testid="still-watching-ad-switch"
                />
              </div>
            </div>
          </div>

          {/* Timeout & Duration Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vast_timeout">VAST Timeout (ms)</Label>
              <Input
                id="vast_timeout"
                type="number"
                min="2000"
                max="15000"
                step="500"
                value={settings?.vast_timeout_ms ?? 5000}
                onChange={(e) => handleChange('vast_timeout_ms', parseInt(e.target.value) || 5000)}
                data-testid="vast-timeout-input"
              />
              <p className="text-xs text-muted-foreground">
                2000-15000ms
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ad_skip_delay">Skip Delay (seconds)</Label>
              <Input
                id="ad_skip_delay"
                type="number"
                min="0"
                max="30"
                value={settings?.ad_skip_delay_seconds ?? 0}
                onChange={(e) => handleChange('ad_skip_delay_seconds', parseInt(e.target.value) || 0)}
                data-testid="ad-skip-delay-input"
              />
              <p className="text-xs text-muted-foreground">
                0 = no skip allowed
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_ad_duration">Max Ad Duration (seconds)</Label>
              <Input
                id="max_ad_duration"
                type="number"
                min="15"
                max="120"
                value={settings?.max_ad_duration_seconds ?? 60}
                onChange={(e) => handleChange('max_ad_duration_seconds', parseInt(e.target.value) || 60)}
                data-testid="max-ad-duration-input"
              />
              <p className="text-xs text-muted-foreground">
                Auto-skip if longer
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Admin Users Tab */}
        <TabsContent value="admins">
          <AdminUsersSection />
        </TabsContent>

        {/* 2FA Settings Tab */}
        <TabsContent value="security">
          <TwoFactorAuthSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Radio Groups for partner admins
const RADIO_GROUPS = [
  { id: 'serfa', name: 'SERFA', description: 'Slots 100-199' },
  { id: 'nerfa', name: 'NERFA', description: 'Slots 400-499' },
];

// Admin Users Section Component
function AdminUsersSection() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    display_name: '',
    email: '',
    radio_groups: [],
    require_2fa: true
  });
  const [isSaving, setIsSaving] = useState(false);

  const fetchAdmins = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getAdminUsers();
      setAdmins(data.users || data || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin users');
      // Show mock data for UI development until backend is ready
      setAdmins([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      display_name: '',
      email: '',
      radio_groups: [],
      require_2fa: true
    });
    setShowPassword(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleEdit = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      username: admin.username,
      password: '',
      display_name: admin.display_name || '',
      email: admin.email || '',
      radio_groups: admin.permissions?.radio_groups || [],
      require_2fa: admin.require_2fa ?? true
    });
    setShowEditDialog(true);
  };

  const handleDelete = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteDialog(true);
  };

  const toggleRadioGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      radio_groups: prev.radio_groups.includes(groupId)
        ? prev.radio_groups.filter(g => g !== groupId)
        : [...prev.radio_groups, groupId]
    }));
  };

  const handleSaveNew = async () => {
    if (!formData.username || !formData.password) {
      toast.error('Username and password are required');
      return;
    }
    if (formData.radio_groups.length === 0) {
      toast.error('Select at least one radio group');
      return;
    }
    
    setIsSaving(true);
    try {
      await api.createAdminUser({
        username: formData.username.toLowerCase(),
        password: formData.password,
        display_name: formData.display_name,
        email: formData.email,
        radio_groups: formData.radio_groups,
        require_2fa: formData.require_2fa
      });
      toast.success('Admin user created');
      setShowAddDialog(false);
      fetchAdmins();
    } catch (err) {
      const errorMessage = typeof err === 'string' ? err : 
        (err?.message || err?.detail || JSON.stringify(err) || 'Failed to create admin user');
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (formData.radio_groups.length === 0) {
      toast.error('Select at least one radio group');
      return;
    }
    
    setIsSaving(true);
    try {
      const updateData = {
        display_name: formData.display_name,
        email: formData.email,
        radio_groups: formData.radio_groups,
        require_2fa: formData.require_2fa
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await api.updateAdminUser(selectedAdmin.username, updateData);
      toast.success('Admin user updated');
      setShowEditDialog(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.message || 'Failed to update admin user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsSaving(true);
    try {
      await api.deleteAdminUser(selectedAdmin.username);
      toast.success('Admin user disabled');
      setShowDeleteDialog(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.message || 'Failed to disable admin user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset2FA = async (admin) => {
    try {
      await api.resetAdminUser2FA(admin.username);
      toast.success(`2FA reset for ${admin.username}`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.message || 'Failed to reset 2FA');
    }
  };

  // Check if current user is super_admin
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_admin;

  if (!isSuperAdmin) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-lg font-medium">Access Restricted</p>
            <p className="text-sm text-muted-foreground">Only super admins can manage admin users</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <p className="font-medium text-blue-400">Partner Admin Management</p>
              <p className="text-sm text-muted-foreground">
                Create accounts for SERFA/NERFA partners to manage their radio feeds. 
                Partner admins can only see and edit feeds assigned to their group.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Users Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Admin Users
              </CardTitle>
              <CardDescription>Partner admins with limited access</CardDescription>
            </div>
            <Button onClick={handleAdd} data-testid="add-admin-btn">
              <UserPlus className="mr-2 h-4 w-4" />
              Add Admin
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-8">
              <AlertCircle className="h-8 w-8 text-yellow-500 mb-3" />
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <p className="text-xs text-muted-foreground mb-4">
                Backend endpoint <code className="px-1 py-0.5 bg-secondary rounded">/api/admin/admin-users</code> may not be implemented yet.
              </p>
              <Button variant="outline" size="sm" onClick={fetchAdmins}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          ) : admins.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">No Partner Admins</p>
              <p className="text-sm text-muted-foreground mb-4">Create your first partner admin to get started</p>
              <Button onClick={handleAdd}>
                <UserPlus className="mr-2 h-4 w-4" />
                Add First Admin
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Radio Access</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.username} data-testid={`admin-row-${admin.username}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{admin.display_name || admin.username}</p>
                        <p className="text-xs text-muted-foreground">@{admin.username}</p>
                        {admin.email && (
                          <p className="text-xs text-muted-foreground">{admin.email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(admin.permissions?.radio_groups || []).map(group => (
                          <Badge 
                            key={group} 
                            variant="outline"
                            className={
                              group === 'serfa' 
                                ? 'border-blue-500/50 text-blue-400' 
                                : 'border-purple-500/50 text-purple-400'
                            }
                          >
                            {group.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {admin.require_2fa ? (
                        admin.totp_enabled ? (
                          <Badge className="bg-green-500/20 text-green-400">
                            <ShieldCheck className="mr-1 h-3 w-3" />
                            Enabled
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400">
                            <ShieldOff className="mr-1 h-3 w-3" />
                            Required
                          </Badge>
                        )
                      ) : (
                        <Badge variant="secondary">Optional</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {admin.enabled !== false ? (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      ) : (
                        <Badge variant="destructive">Disabled</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {admin.last_login 
                        ? new Date(admin.last_login).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(admin)}
                          data-testid={`edit-admin-${admin.username}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {admin.totp_enabled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReset2FA(admin)}
                            title="Reset 2FA"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(admin)}
                          data-testid={`delete-admin-${admin.username}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Add Partner Admin
            </DialogTitle>
            <DialogDescription>
              Create a new admin account for a radio partner
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="serfa_admin"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Secure password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name</Label>
                <Input
                  id="display_name"
                  placeholder="SERFA Administrator"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@serfa.org"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Radio Group Access</Label>
              <div className="flex gap-2">
                {RADIO_GROUPS.map(group => (
                  <Button
                    key={group.id}
                    type="button"
                    variant={formData.radio_groups.includes(group.id) ? 'default' : 'outline'}
                    onClick={() => toggleRadioGroup(group.id)}
                    className="flex-1"
                  >
                    {formData.radio_groups.includes(group.id) && <CheckCircle className="mr-2 h-4 w-4" />}
                    <Radio className="mr-2 h-4 w-4" />
                    {group.name}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                User will only see feeds from selected groups
              </p>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
              <div>
                <p className="font-medium">Require 2FA</p>
                <p className="text-sm text-muted-foreground">User must set up 2FA on first login</p>
              </div>
              <Switch
                checked={formData.require_2fa}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, require_2fa: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveNew} disabled={isSaving}>
              {isSaving ? 'Creating...' : 'Create Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-primary" />
              Edit Admin: {selectedAdmin?.username}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_display_name">Display Name</Label>
                <Input
                  id="edit_display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_password">New Password (leave blank to keep current)</Label>
              <div className="relative">
                <Input
                  id="edit_password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Leave blank to keep current"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Radio Group Access</Label>
              <div className="flex gap-2">
                {RADIO_GROUPS.map(group => (
                  <Button
                    key={group.id}
                    type="button"
                    variant={formData.radio_groups.includes(group.id) ? 'default' : 'outline'}
                    onClick={() => toggleRadioGroup(group.id)}
                    className="flex-1"
                  >
                    {formData.radio_groups.includes(group.id) && <CheckCircle className="mr-2 h-4 w-4" />}
                    <Radio className="mr-2 h-4 w-4" />
                    {group.name}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
              <div>
                <p className="font-medium">Require 2FA</p>
                <p className="text-sm text-muted-foreground">User must set up 2FA on first login</p>
              </div>
              <Switch
                checked={formData.require_2fa}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, require_2fa: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Admin Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disable the account for <strong>{selectedAdmin?.display_name || selectedAdmin?.username}</strong>. 
              They will no longer be able to log in. You can re-enable the account later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disable Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// 2FA Section Component
function TwoFactorAuthSection() {
  const [status, setStatus] = useState({ enabled: false, has_backup_codes: false });
  const [isLoading, setIsLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const fetchStatus = async () => {
    setIsLoading(true);
    try {
      const data = await api.get2FAStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStartSetup = async () => {
    setIsProcessing(true);
    try {
      const data = await api.setup2FA();
      setSetupData(data);
    } catch (err) {
      toast.error(err.message || 'Failed to start 2FA setup');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setIsProcessing(true);
    try {
      const result = await api.verifySetup2FA(verifyCode);
      setBackupCodes(result.backup_codes);
      setStatus({ enabled: true, has_backup_codes: true });
      setSetupData(null);
      setVerifyCode('');
      toast.success('Two-Factor Authentication enabled!');
    } catch (err) {
      toast.error(err.message || 'Invalid verification code');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter your current 6-digit code');
      return;
    }
    setIsProcessing(true);
    try {
      await api.disable2FA(disableCode);
      setStatus({ enabled: false, has_backup_codes: false });
      setShowDisableConfirm(false);
      setDisableCode('');
      toast.success('Two-Factor Authentication disabled');
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  };

  if (isLoading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="animate-pulse h-20 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Shield className="h-5 w-5 text-primary" />
          Two-Factor Authentication (2FA)
        </CardTitle>
        <CardDescription>
          Add an extra layer of security to your admin account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
          <div className="flex items-center gap-3">
            {status.enabled ? (
              <ToggleRight className="h-8 w-8 text-green-400" />
            ) : (
              <ToggleLeft className="h-8 w-8 text-muted-foreground" />
            )}
            <div>
              <p className="font-medium text-white">
                {status.enabled ? '2FA is Enabled' : '2FA is Disabled'}
              </p>
              <p className="text-sm text-muted-foreground">
                {status.enabled 
                  ? 'Your account is protected with two-factor authentication'
                  : 'Enable 2FA to secure your admin account'}
              </p>
            </div>
          </div>
          <Badge variant={status.enabled ? 'default' : 'secondary'} className={status.enabled ? 'bg-green-500/20 text-green-400' : ''}>
            {status.enabled ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        {/* Setup Flow */}
        {!status.enabled && !setupData && (
          <Button onClick={handleStartSetup} disabled={isProcessing}>
            {isProcessing ? 'Setting up...' : 'Enable Two-Factor Authentication'}
          </Button>
        )}

        {/* QR Code Display */}
        {setupData && !backupCodes && (
          <div className="space-y-4 p-4 rounded-lg border border-primary/30 bg-primary/5">
            <h4 className="font-medium text-white">Step 1: Scan QR Code</h4>
            <p className="text-sm text-muted-foreground">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            <div className="flex justify-center py-4">
              <img 
                src={setupData.qr_code} 
                alt="2FA QR Code" 
                className="border border-border rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Or enter this code manually:</p>
              <code className="block p-2 bg-black/30 rounded text-sm font-mono text-primary break-all">
                {setupData.secret}
              </code>
            </div>
            
            <h4 className="font-medium text-white mt-6">Step 2: Enter Verification Code</h4>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code from your authenticator app to verify setup
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono text-lg tracking-widest text-center max-w-[150px]"
                maxLength={6}
              />
              <Button onClick={handleVerifySetup} disabled={isProcessing || verifyCode.length !== 6}>
                {isProcessing ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </div>
          </div>
        )}

        {/* Backup Codes Display */}
        {backupCodes && (
          <div className="space-y-4 p-4 rounded-lg border border-green-500/30 bg-green-500/5">
            <h4 className="font-medium text-green-400 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Save Your Backup Codes!
            </h4>
            <p className="text-sm text-muted-foreground">
              Store these codes somewhere safe. Each code can only be used once if you lose access to your authenticator.
            </p>
            <div className="grid grid-cols-2 gap-2 p-3 bg-black/30 rounded-lg font-mono text-sm">
              {backupCodes.map((code, i) => (
                <div key={i} className="text-white">{code}</div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={copyBackupCodes}>
              Copy Backup Codes
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setBackupCodes(null)}>
              I've saved my codes
            </Button>
          </div>
        )}

        {/* Disable 2FA */}
        {status.enabled && !showDisableConfirm && (
          <div className="pt-4 border-t border-border">
            <Button 
              variant="outline" 
              className="text-red-400 border-red-400/30 hover:bg-red-500/10"
              onClick={() => setShowDisableConfirm(true)}
            >
              Disable Two-Factor Authentication
            </Button>
          </div>
        )}

        {showDisableConfirm && (
          <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5 space-y-4">
            <h4 className="font-medium text-red-400">Confirm Disable 2FA</h4>
            <p className="text-sm text-muted-foreground">
              Enter your current authenticator code to disable 2FA
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono text-lg tracking-widest text-center max-w-[150px]"
                maxLength={6}
              />
              <Button 
                variant="destructive" 
                onClick={handleDisable} 
                disabled={isProcessing || disableCode.length !== 6}
              >
                {isProcessing ? 'Disabling...' : 'Disable 2FA'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowDisableConfirm(false); setDisableCode(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default Settings;
